// Language utilities for URL-based language routing

export const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'de', 'pt'];
export const DEFAULT_LANGUAGE = 'en';

/**
 * Validate and normalize a language code
 * @param {string} langCode - The language code to validate
 * @returns {string} - A valid language code
 */
export function validateLanguageCode(langCode) {
  if (typeof langCode !== 'string') {
    return DEFAULT_LANGUAGE;
  }
  
  const normalizedCode = langCode.toLowerCase().trim();
  return SUPPORTED_LANGUAGES.includes(normalizedCode) ? normalizedCode : DEFAULT_LANGUAGE;
}

/**
 * Extract language from URL path
 * @param {string} pathname - The current pathname
 * @returns {string} - The language code (en, es, fr) or null if not found
 */
export function getLanguageFromPath(pathname) {
  const pathSegments = pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];
  
  if (SUPPORTED_LANGUAGES.includes(firstSegment)) {
    return firstSegment;
  }
  
  return null;
}

/**
 * Get the current language from URL or localStorage
 * @param {string} pathname - The current pathname
 * @returns {string} - The current language code
 */
export function getCurrentLanguage(pathname) {
  // First check URL path
  const urlLanguage = getLanguageFromPath(pathname);
  
  if (urlLanguage) {
    return urlLanguage;
  }
  
  // Then check localStorage
  const storedLanguage = localStorage.getItem('selectedLanguage');
  if (storedLanguage && SUPPORTED_LANGUAGES.includes(storedLanguage)) {
    return storedLanguage;
  }
  
  // Finally check navigator.language
  const browserLanguage = navigator.language?.split('-')[0];
  if (browserLanguage && SUPPORTED_LANGUAGES.includes(browserLanguage)) {
    return browserLanguage;
  }
  
  return DEFAULT_LANGUAGE;
}

/**
 * Create a localized URL
 * @param {string} path - The path without language prefix
 * @param {string} language - The language code
 * @returns {string} - The localized URL
 */
export function createLocalizedUrl(path, language) {
  // Normalize input path
  const safePath = typeof path === 'string' ? path : '';
  const cleanPath = safePath && safePath.startsWith('/') ? safePath : `/${safePath || ''}`;

  // English (default) should be neutral (no /en prefix)
  if (language === DEFAULT_LANGUAGE) {
    return cleanPath || '/';
  }

  return `/${language}${cleanPath}`;
}

/**
 * Remove language prefix from URL
 * @param {string} pathname - The current pathname
 * @returns {string} - The path without language prefix
 */
export function removeLanguagePrefix(pathname) {
  const pathSegments = pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0];
  
  if (SUPPORTED_LANGUAGES.includes(firstSegment)) {
    return '/' + pathSegments.slice(1).join('/');
  }
  
  return pathname;
}

/**
 * Get hreflang links for SEO
 * @param {string} currentPath - The current path without language prefix
 * @returns {Array} - Array of hreflang objects
 */
export function getHreflangLinks(currentPath) {
  return SUPPORTED_LANGUAGES.map(lang => ({
    rel: 'alternate',
    hreflang: lang,
    href: `https://livelovespain.com${createLocalizedUrl(currentPath, lang)}`
  }));
}
